import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CardapioPedidoDTO, CardapioItemCarrinhoDTO } from '../../models/cardapio-digital/cardapio-digital.model';
import { TipoPedido } from '../../enums/TipoPedido.enum';

/**
 * Servico para geracao de mensagem formatada e link WhatsApp.
 */
@Injectable({ providedIn: 'root' })
export class CardapioWhatsappService {

  constructor(private translate: TranslateService) {}

  /** Retorna traducao de uma chave dentro do bloco cardapio_digital.whatsapp */
  private t(chave: string): string {
    return this.translate.instant(`cardapio_digital.whatsapp.${chave}`);
  }

  gerarMensagem(pedido: CardapioPedidoDTO, moedaSimbolo: string, moedaLocal: string): string {
    const linhas: string[] = [];

    linhas.push(`*${this.t('pedido_numero')} #${pedido.idCurto}*`);
    linhas.push('');

    // Itens
    pedido.itens.forEach((item: CardapioItemCarrinhoDTO) => {
      const precoFormatado = this.formatarPreco(item.precoTotal, moedaSimbolo, moedaLocal);
      linhas.push(`${item.quantidade}x ${item.nome} - ${precoFormatado}`);

      // Opcoes selecionadas
      if (item.opcoesSelecionadas && item.opcoesSelecionadas.length > 0) {
        item.opcoesSelecionadas.forEach(opcao => {
          const precoOpcao = opcao.precoAdicional > 0
            ? ` (+${this.formatarPreco(opcao.precoAdicional, moedaSimbolo, moedaLocal)})`
            : '';
          linhas.push(`  _+ ${opcao.nome}${precoOpcao}_`);
        });
      }

      if (item.observacao) {
        linhas.push(`  _Obs: ${item.observacao}_`);
      }
    });

    linhas.push('');
    linhas.push(`*${this.t('subtotal')}:* ${this.formatarPreco(pedido.subtotal, moedaSimbolo, moedaLocal)}`);

    if (pedido.tipoPedido === TipoPedido.DELIVERY) {
      const freteTexto = pedido.frete > 0
        ? this.formatarPreco(pedido.frete, moedaSimbolo, moedaLocal)
        : this.t('frete_gratis');
      linhas.push(`*${this.t('frete')}:* ${freteTexto}`);
    }

    linhas.push(`*${this.t('total')}:* ${this.formatarPreco(pedido.total, moedaSimbolo, moedaLocal)}`);
    linhas.push('');

    // Tipo de pedido e dados
    switch (pedido.tipoPedido) {
      case TipoPedido.DELIVERY:
        linhas.push(`*${this.t('tipo')}:* ${this.t('tipo_delivery')}`);
        linhas.push(`*${this.t('nome')}:* ${pedido.nomeCliente}`);
        if (pedido.telefone) linhas.push(`*${this.t('telefone')}:* ${pedido.telefone}`);
        if (pedido.endereco) linhas.push(`*${this.t('endereco')}:* ${pedido.endereco}`);
        break;
      case TipoPedido.RETIRADA:
        linhas.push(`*${this.t('tipo')}:* ${this.t('tipo_retirada')}`);
        linhas.push(`*${this.t('nome')}:* ${pedido.nomeCliente}`);
        if (pedido.telefone) linhas.push(`*${this.t('telefone')}:* ${pedido.telefone}`);
        break;
      case TipoPedido.MESA:
        linhas.push(`*${this.t('tipo')}:* ${this.t('tipo_mesa')}`);
        linhas.push(`*${this.t('nome')}:* ${pedido.nomeCliente}`);
        if (pedido.telefone) linhas.push(`*${this.t('telefone')}:* ${pedido.telefone}`);
        if (pedido.numeroMesa) linhas.push(`*${this.t('mesa_numero')}:* ${pedido.numeroMesa}`);
        break;
    }

    // Comentario do cliente
    if (pedido.comentario) {
      linhas.push('');
      linhas.push(`*${this.t('comentario')}:* ${pedido.comentario}`);
    }

    // Forma de pagamento
    if (pedido.formaPagamento) {
      const formaPgto = pedido.formaPagamento === 'DINHEIRO'
        ? this.t('pagamento_dinheiro')
        : this.t('pagamento_cartao');
      linhas.push(`*${this.t('pagamento')}:* ${formaPgto}`);

      // Valor pago e troco (apenas pagamento em dinheiro)
      if (pedido.formaPagamento === 'DINHEIRO' && pedido.valorPago) {
        linhas.push(`*${this.t('paga_com')}:* ${this.formatarPreco(pedido.valorPago, moedaSimbolo, moedaLocal)}`);
        if (pedido.troco !== undefined && pedido.troco > 0) {
          linhas.push(`*${this.t('troco')}:* ${this.formatarPreco(pedido.troco, moedaSimbolo, moedaLocal)}`);
        } else if (pedido.troco === 0) {
          linhas.push(`*${this.t('troco')}:* ${this.t('sem_troco')}`);
        }
      }
    }

    return linhas.join('\n');
  }

  gerarLinkWhatsapp(telefone: string, mensagem: string): string {
    const telefoneLimpo = telefone.replace(/\D/g, '');
    const mensagemCodificada = encodeURIComponent(mensagem);
    return `https://wa.me/${telefoneLimpo}?text=${mensagemCodificada}`;
  }

  abrirWhatsapp(telefone: string, pedido: CardapioPedidoDTO, moedaSimbolo: string, moedaLocal: string): void {
    const mensagem = this.gerarMensagem(pedido, moedaSimbolo, moedaLocal);
    const link = this.gerarLinkWhatsapp(telefone, mensagem);
    window.open(link, '_blank');
  }

  gerarIdCurto(): string {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  }

  private formatarPreco(valor: number, moedaSimbolo: string, moedaLocal: string): string {
    try {
      return new Intl.NumberFormat(moedaLocal, {
        style: 'currency',
        currency: moedaSimbolo
      }).format(valor);
    } catch {
      return `${moedaSimbolo} ${valor.toFixed(2)}`;
    }
  }
}
